import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  ButtonGroup,
  ToggleButton,
} from "react-bootstrap";
import "../Accounts/CreateInvoice.css";

/* ---------------- utils ---------------- */
const pad = (n) => String(n).padStart(2, "0");
const toDateOnly = (iso) => (iso || "").slice(0, 10);
const to24h = (h12, ap) => {
  let h = parseInt(h12 || "12", 10);
  if (ap === "AM") return h % 12;
  return (h % 12) + 12;
};
const joinDateTime = (date, hh, mm, ap) => {
  if (!date || !hh || !mm || !ap) return null;
  return `${date}T${pad(to24h(hh, ap))}:${pad(mm)}`;
};

const normId = (a) => String(a?._id?.$oid || a?._id || a?.id || "");
const normStartISO = (a) =>
  a?.start ||
  a?.startStr ||
  (a?.startDate && a?.time && `${a.startDate}T${a.time}`) ||
  (a?.startDate && `${a.startDate}T00:00`) ||
  a?.appointmentStartSet ||
  "";
const normEndISO = (a, startISO) => {
  const direct =
    a?.end ||
    a?.endStr ||
    (a?.endDate && a?.endTime && `${a.endDate}T${a.endTime}`) ||
    (a?.startDate && a?.endTime && `${a.startDate}T${a.endTime}`) ||
    a?.appointmentEndSet ||
    "";
  if (direct) return direct;

  if (startISO && typeof a?.durationMinutes === "number") {
    const d = new Date(startISO);
    d.setMinutes(d.getMinutes() + a.durationMinutes);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  }
  return "";
};

/* ---------------- calendar ---------------- */
function CalendarCard({ label, value, onChange }) {
  const today = new Date();
  const [view, setView] = React.useState(() => {
    const d = value ? new Date(value) : today;
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const first = new Date(view.y, view.m, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();

  const prevMonth = () => {
    const m = view.m === 0 ? 11 : view.m - 1;
    const y = view.m === 0 ? view.y - 1 : view.y;
    setView({ y, m });
  };
  const nextMonth = () => {
    const m = view.m === 11 ? 0 : view.m + 1;
    const y = view.m === 11 ? view.y + 1 : view.y;
    setView({ y, m });
  };

  const monthName = new Date(view.y, view.m, 1).toLocaleString("en-ZA", {
    month: "long",
    year: "numeric",
  });

  const cells = [];
  for (let i = 0; i < (startDow === 0 ? 6 : startDow - 1); i++)
    cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedKey = value
    ? `${new Date(value).getFullYear()}-${new Date(value).getMonth()}-${new Date(
        value
      ).getDate()}`
    : "";

  return (
    <div className="appt-card">
      <div className="appt-card__head d-flex justify-content-between align-items-center">
        <span className="appt-card__title">{label}</span>
        <div className="appt-card__nav">
          <Button
            size="sm"
            variant="light"
            className="rounded-0"
            onClick={prevMonth}
          >
            ‹
          </Button>
          <span className="mx-2 fw-semibold">{monthName}</span>
          <Button
            size="sm"
            variant="light"
            className="rounded-0"
            onClick={nextMonth}
          >
            ›
          </Button>
        </div>
      </div>

      <div className="calendar-grid">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="calendar-grid__dow">
            {d}
          </div>
        ))}

        {cells.map((d, idx) => {
          if (d === null)
            return (
              <div key={`b${idx}`} className="calendar-grid__cell is-blank" />
            );
          const selKey = `${view.y}-${view.m}-${d}`;
          const active = selKey === selectedKey;
          const dateStr = `${view.y}-${pad(view.m + 1)}-${pad(d)}`;
          return (
            <button
              key={selKey}
              type="button"
              className={`calendar-grid__cell btn btn-light rounded-0 ${active ? "is-active" : ""}`}
              onClick={() => onChange?.(dateStr)}
            >
              {pad(d)}
            </button>
          );
        })}
      </div>

      <div className="appt-card__footer">
        <Button
          size="sm"
          variant="outline-secondary"
          className="rounded-0"
          onClick={() => {
            const t = new Date();
            const ds = `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
            onChange?.(ds);
          }}
        >
          Today
        </Button>
      </div>
    </div>
  );
}

/* ---------------- compact time picker (no clock) ---------------- */
function TimeCard({ label, hh, mm, ap, onChange }) {
  const hours = Array.from({ length: 12 }, (_, i) => pad(i + 1));
  const minutes = Array.from({ length: 60 }, (_, i) => pad(i)); // 00..59
  return (
    <div className="appt-card">
      <div className="appt-card__head">
        <span className="appt-card__title">{label}</span>
      </div>
      <div className="d-flex gap-2 justify-content-center px-3 py-3">
        <Form.Select
          size="sm"
          className="rounded-0 w-auto"
          value={hh}
          onChange={(e) => onChange({ hh: e.target.value, mm, ap })}
        >
          {hours.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          size="sm"
          className="rounded-0 w-auto"
          value={mm}
          onChange={(e) => onChange({ hh, mm: e.target.value, ap })}
        >
          {minutes.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          size="sm"
          className="rounded-0 w-auto"
          value={ap}
          onChange={(e) => onChange({ hh, mm, ap: e.target.value })}
        >
          <option>AM</option>
          <option>PM</option>
        </Form.Select>
      </div>
    </div>
  );
}

/* ---------------- modal ---------------- */
export default function InvoiceAppointmentModal({
  show,
  onHide,
  patientId,
  initial,
  fetchAppointments,
  onConfirm,
  error = "",
}) {
  console.log("initial", initial);
  const [mode, setMode] = useState(
    initial?.useCustomDate ? "custom" : "appointment"
  );

  // appointments
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [appointmentId, setAppointmentId] = useState(
    initial?.appointmentId || ""
  );
  const [err, setErr] = useState("");

  // custom
  const startInit =
    initial?.appointmentCustomStart || initial?.appointmentStartSet || "";
  const endInit =
    initial?.appointmentCustomEnd || initial?.appointmentEndSet || "";

  // defaults (rounded to nearest 5)
  const now = new Date();
  const currentDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const currentMinute = now.getMinutes();
  const roundedMinute = Math.round(currentMinute / 5) * 5;
  const adjustedTime = new Date(now);
  adjustedTime.setMinutes(roundedMinute);
  if (adjustedTime.getMinutes() === 60) {
    adjustedTime.setHours(adjustedTime.getHours() + 1);
    adjustedTime.setMinutes(0);
  }
  const defaultStartHour = adjustedTime.getHours();
  const defaultStartMinute = adjustedTime.getMinutes();
  const defaultEndTime = new Date(adjustedTime);
  defaultEndTime.setHours(defaultEndTime.getHours() + 1);
  const defaultEndHour = defaultEndTime.getHours();
  const defaultEndMinute = defaultEndTime.getMinutes();

  const [startDate, setStartDate] = React.useState(
    toDateOnly(startInit) || currentDate
  );
  const [useDifferentEndDate, setUseDifferentEndDate] = React.useState(
    !!(endInit && toDateOnly(endInit) !== toDateOnly(startInit))
  );
  const [endDate, setEndDate] = React.useState(
    toDateOnly(endInit) || currentDate
  );

  // "No Times" mode — when on, only the date(s) matter; times are skipped.
  // Auto-detect from initial values: if both stored times are midnight, treat as no-times.
  const [noTimes, setNoTimes] = React.useState(() => {
    if (!startInit || !endInit) return false;
    const s = new Date(startInit);
    const e = new Date(endInit);
    return (
      s.getHours() === 0 &&
      s.getMinutes() === 0 &&
      e.getHours() === 0 &&
      e.getMinutes() === 0
    );
  });

  const [startT, setStartT] = React.useState({
    hh: startInit
      ? pad(new Date(startInit).getHours() % 12 || 12)
      : pad(defaultStartHour % 12 || 12),
    mm: startInit
      ? pad(new Date(startInit).getMinutes())
      : pad(defaultStartMinute),
    ap: startInit
      ? new Date(startInit).getHours() >= 12
        ? "PM"
        : "AM"
      : defaultStartHour >= 12
        ? "PM"
        : "AM",
  });
  const [endT, setEndT] = React.useState({
    hh: endInit
      ? pad(new Date(endInit).getHours() % 12 || 12)
      : pad(defaultEndHour % 12 || 12),
    mm: endInit ? pad(new Date(endInit).getMinutes()) : pad(defaultEndMinute),
    ap: endInit
      ? new Date(endInit).getHours() >= 12
        ? "PM"
        : "AM"
      : defaultEndHour >= 12
        ? "PM"
        : "AM",
  });

  // load appointments
  React.useEffect(() => {
    let cancel = false;
    (async () => {
      if (!patientId || !fetchAppointments || !show) return;
      setLoading(true);
      setErr("");
      try {
        const data = await fetchAppointments(patientId);
        const arr = Array.isArray(data) ? data : [];
        const normalized = arr.map((a) => {
          const __id = normId(a);
          const __startISO = normStartISO(a);
          const __endISO = normEndISO(a, __startISO);
          return { ...a, __id, __startISO, __endISO };
        });
        console.log("fetched appointments", normalized);
        if (!cancel) setList(normalized);
      } catch {
        if (!cancel) setErr("Could not load appointments.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [patientId, fetchAppointments, show]);

  React.useEffect(() => {
    if (initial?.appointmentId && list.length > 0) {
      const match = list.find(
        (a) => normId(a) === String(initial.appointmentId)
      );
      if (match) {
        setAppointmentId(normId(match));
        setMode(initial.useCustomDate ? "custom" : "appointment");
      }
    }
  }, [initial, list]);
  console.log("fetched list", list);

  const sorted = React.useMemo(
    () =>
      [...list].sort(
        (a, b) => Date.parse(b.__startISO || 0) - Date.parse(a.__startISO || 0)
      ),
    [list]
  );

  const fmtOption = (a) => {
    const s = a.__startISO,
      e = a.__endISO;
    const sD = s ? new Date(s) : null,
      eD = e ? new Date(e) : null;
    const date = sD
      ? sD.toLocaleDateString("en-ZA", {
          year: "2-digit",
          month: "short",
          day: "numeric",
        })
      : "—";
    const sT = sD
      ? sD.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })
      : "—";
    const eT = eD
      ? eD.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })
      : "—";
    return `${date} @ ${sT} – ${eT}`;
  };

  const confirm = () => {
    setErr("");
    if (mode === "appointment") {
      if (!appointmentId) return setErr("Please select an appointment.");
      const appt = list.find((a) => a.__id === appointmentId);
      if (!appt) return setErr("Appointment not found.");

      const s = appt.__startISO || null;
      const e = appt.__endISO || null;

      onConfirm?.({
        useCustomDate: false,
        appointmentId,
        appointmentStartSet: s,
        appointmentEndSet: e,
        appointmentDateSet: s ? toDateOnly(s) : null,
        appointmentCustomStart: null,
        appointmentCustomEnd: null,
        appointmentDateCustom: null,
        appointmentDate: s ? toDateOnly(s) : null,
        isInvoiced: !!appt.isInvoiced,
      });
      onHide?.();
      return;
    }

    // custom
    if (!startDate) return setErr("Please select a start date.");
    const endDateToUse = useDifferentEndDate ? endDate : startDate;

    let startISO, endISO;
    if (noTimes) {
      // Date-only: store midnight for both ends so downstream date-only consumers
      // (getLineServiceDate, appointmentDateCustom) read the right calendar day.
      startISO = `${startDate}T00:00`;
      endISO = `${endDateToUse}T00:00`;
    } else {
      if (!startT.hh || !startT.mm || !startT.ap)
        return setErr("Please set a start time.");
      if (!endT.hh || !endT.mm || !endT.ap)
        return setErr("Please set an end time.");
      startISO = joinDateTime(startDate, startT.hh, startT.mm, startT.ap);
      endISO = joinDateTime(endDateToUse, endT.hh, endT.mm, endT.ap);
      if (!startISO || !endISO) {
        return setErr("Invalid start or end date/time.");
      }
    }

    const sMs = Date.parse(startISO);
    const eMs = Date.parse(endISO);

    if (Number.isNaN(sMs) || Number.isNaN(eMs)) {
      return setErr("Invalid start or end date/time.");
    }

    if (noTimes) {
      if (eMs < sMs) return setErr("End date must be on or after start date.");
    } else {
      if (eMs <= sMs) return setErr("End time must be after start time.");
    }

    onConfirm?.({
      useCustomDate: true,
      appointmentId: null,
      appointmentStartSet: null,
      appointmentEndSet: null,
      appointmentDateSet: null,
      appointmentCustomStart: startISO,
      appointmentCustomEnd: endISO,
      appointmentDateCustom: toDateOnly(startISO),
      appointmentDate: toDateOnly(startISO),
    });
    onHide?.();
  };

  // ---------------- NEW LAYOUT FOR CUSTOM MODE (single row) ----------------
  const showingEndCalendar = useDifferentEndDate; // if true, show End Date calendar in the same slot

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Select Appointment or Treatment Date
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {show && !patientId && (
          <Alert variant="warning" className="mb-3">
            Please select a patient first.
          </Alert>
        )}

        {/* mode toggle */}
        <div className="d-flex justify-content-center mb-4">
          <ButtonGroup className="gap-3">
            <ToggleButton
              id="mode-appointment"
              type="radio"
              variant={mode === "appointment" ? "primary" : "outline-primary"}
              value="appointment"
              checked={mode === "appointment"}
              onChange={() => setMode("appointment")}
              className="rounded-0 btn-sm px-4 py-2"
            >
              Link Appointment
            </ToggleButton>
            <ToggleButton
              id="mode-custom"
              type="radio"
              variant={mode === "custom" ? "primary" : "outline-primary"}
              value="custom"
              checked={mode === "custom"}
              onChange={() => setMode("custom")}
              className="rounded-0 btn-sm px-4 py-2"
            >
              Custom Date & Time
            </ToggleButton>
          </ButtonGroup>
        </div>

        {/* appointment dropdown */}
        <div
          className={`mode-block ${mode !== "appointment" ? "is-muted" : ""}`}
        >
          <Form.Label className="fw-bold mb-1">Patient Appointments</Form.Label>
          <Form.Select
            size="sm"
            className="rounded-0"
            value={appointmentId}
            onChange={(e) => {
              setAppointmentId(e.target.value);
              setMode("appointment");
            }}
            disabled={!patientId || loading}
          >
            {loading ? (
              <option value="">Loading…</option>
            ) : sorted.length === 0 ? (
              <option value="">No appointments</option>
            ) : (
              <>
                <option value="">Select appointment…</option>
                {sorted.map((a) => (
                  <option key={a.__id} value={a.__id}>
                    {fmtOption(a)}
                  </option>
                ))}
              </>
            )}
          </Form.Select>
          {loading && <Spinner animation="border" size="sm" className="mt-2" />}
          {(() => {
            const selectedAppt = list.find((a) => a.__id === appointmentId);
            if (selectedAppt?.isInvoiced) {
              return (
                <div className="text-warning small mt-2">
                  <i className="fa-solid fa-triangle-exclamation me-1"></i>
                  This appointment was already invoiced before.
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* OR */}
        <div className="or-divider my-4">
          <span>OR</span>
        </div>

        {/* CUSTOM: single-row layout */}
        <div className={`mode-block ${mode !== "custom" ? "is-muted" : ""}`}>
          <div className="row align-items-stretch">
            {/* Left: calendar that switches between Start/End based on toggle */}
            <div className="col-md-6 mb-3">
              <CalendarCard
                style={{ borderRadius: 0 }}
                label={showingEndCalendar ? "End Date" : "Start Date"}
                value={showingEndCalendar ? endDate : startDate}
                onChange={(d) => {
                  if (showingEndCalendar) {
                    setEndDate(d);
                  } else {
                    setStartDate(d);
                    if (!useDifferentEndDate) setEndDate(d);
                  }
                  setMode("custom");
                }}
              />
            </div>

            {/* Right: No Times toggle -> Start Time -> End-date toggle -> End Time (stacked) */}
            <div className="col-md-6 mb-3 d-flex flex-column gap-3">
              <div className="px-3 pt-2">
                <Form.Check
                  type="checkbox"
                  id="no-times"
                  label="No times (date only)"
                  checked={noTimes}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setNoTimes(on);
                    // Enabling date-only collapses the range to a single date,
                    // so reset the "different end date" toggle and sync endDate.
                    if (on) {
                      setUseDifferentEndDate(false);
                      setEndDate(startDate);
                    }
                    setMode("custom");
                  }}
                />
              </div>

              {!noTimes && (
                <TimeCard
                  label="Start Time"
                  hh={startT.hh}
                  mm={startT.mm}
                  ap={startT.ap}
                  onChange={(t) => {
                    setStartT(t);
                    setMode("custom");
                  }}
                />
              )}

              {!noTimes && (
                <div className="px-3 py-3">
                  <Form.Check
                    type="switch"
                    id="different-end-date"
                    label="Use different end date"
                    checked={useDifferentEndDate}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setUseDifferentEndDate(on);
                      if (!on) {
                        setEndDate(startDate);
                      } else if (!endDate) {
                        setEndDate(startDate); // ✅ ensure it is never empty
                      }
                    }}
                  />
                  {!useDifferentEndDate && (
                    <div className="mt-2 small text-muted">
                      Same as start date: <strong>{startDate || "—"}</strong>
                    </div>
                  )}
                </div>
              )}

              {!noTimes && (
                <TimeCard
                  label="End Time"
                  hh={endT.hh}
                  mm={endT.mm}
                  ap={endT.ap}
                  onChange={(t) => {
                    setEndT(t);
                    setMode("custom");
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {err && <div className="text-danger mt-3">{err}</div>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} className="rounded-0">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={confirm}
          disabled={!patientId}
          className="rounded-0"
        >
          Use for Invoice
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
